import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { buildMetaTags, resolveSeo, SITE } from "@/utils/seo";

/**
 * Automatically generates and injects per-page SEO metadata (title, meta
 * description, keywords, canonical URL and Open Graph / Twitter tags) for
 * every route from the central config in src/config/seo.ts.
 *
 * It is rendered inside SiteLayout BEFORE <main>, so a page that still passes
 * its own <PageMeta> overrides will win (React runs child effects after this
 * one), while every other route gets good metadata for free.
 */
export default function AutoSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const entry = resolveSeo(pathname);
    const tags = buildMetaTags(entry, pathname);
    const path = pathname.split("?")[0].split("#")[0].replace(/\/+$/, "");
    const url = path === "" || path === "/" ? `${SITE}/` : `${SITE}${path}`;

    const cleanup = applyTags(tags, entry.title, url);
    return cleanup;
  }, [pathname]);

  return null;
}

/** Injects the tags into <head> and returns a cleanup that restores the DOM. */
function applyTags(
  tags: { attr: "name" | "property"; key: string; content: string }[],
  title: string,
  url: string,
): () => void {
  const created: HTMLMetaElement[] = [];
  const restored: { el: HTMLMetaElement; previous: string | null }[] = [];

  const prevTitle = document.title;
  document.title = title;

  tags.forEach(({ attr, key, content }) => {
    if (!content) return;
    const selector = `meta[${attr}="${key}"]`;
    let el = document.head.querySelector<HTMLMetaElement>(selector);
    if (el) {
      restored.push({ el, previous: el.getAttribute("content") });
    } else {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
      created.push(el);
    }
    el.setAttribute("content", content);
  });

  const canonicalSelector = 'link[rel="canonical"]';
  let canonical = document.head.querySelector<HTMLLinkElement>(canonicalSelector);
  let createdCanonical = false;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
    createdCanonical = true;
  }
  canonical.setAttribute("href", url);

  return () => {
    document.title = prevTitle;
    created.forEach((el) => el.remove());
    restored.forEach(({ el, previous }) => {
      if (previous === null) el.removeAttribute("content");
      else el.setAttribute("content", previous);
    });
    if (createdCanonical && canonical) canonical.remove();
  };
}